import isBoolean from 'lodash/isBoolean';
import { apiPOST } from './networking';

// TODO: consider creating useEvaluation hook later
//       and wrap the following items into it

export const RECOMMENDATION_DEFAULT_NAME = 'Evaluation';

/**
 *
 * @param {import('../store/reducers/dashboardReducer').TEvaluationDetails|null} recommendation
 * @returns {boolean}
 */
export const isObservation = (recommendation) => {
  return recommendation?.recommendationRequest?.evaluationType === 'Observation Hours';
}

/**
 *
 * @param {import('../store/reducers/dashboardReducer').TEvaluationDetails|null} recommendation
 * @returns {boolean}
 */
export const isCommittee = (recommendation) => {
  return recommendation?.recommendationRequest?.evaluationType === 'Committee';
}


/**
 *
 * @param {number} id
 * @param {?boolean=} reUseResponseForFuture
 * @returns string
 */
const buildCompletedRecommendationPath = (id, reUseResponseForFuture) => {
  let result = `/recommendation/rest/completeRecommendation/${id}`;
  if (isBoolean(reUseResponseForFuture)) {
    result = result + `?reuseForFutureEvals=${reUseResponseForFuture}`;
  }
  return result;
};

/**
 *
 * @param {number=} id
 * @param {?boolean=} reUseResponseForFuture
 * @returns {Promise<boolean>}
 */
export function sendCompletedRecommendation(id, reUseResponseForFuture) {
  const path = id ? buildCompletedRecommendationPath(id, reUseResponseForFuture) : null;
  // uncomment this when you want to test the submissoin flow without actual submitting a request
  // return new Promise((r) => { setTimeout(r, 5000); });
  return path
    ? apiPOST(path)
      .then(response => {
        return response.body.statusCode === 200 && response.body.status === 'OK';
      })
      .catch(error => {
        console.error(error);
        return false;
      })
    : Promise.reject(false);
}

export class RecommendationStatus {
  static get COMPLETED() {
    return 'completed';
  }
  static get REQUESTED() {
    return 'requested';
  }
  static get ACCEPTED() {
    return 'accepted';
  }
  static get DECLINED() {
    return 'declined';
  }
  static get EXPIRED() {
    return 'expired';
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {boolean}
   */
  static isCompleted(request) {
    return request.status === RecommendationStatus.COMPLETED;
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {boolean}
   */
  static isRequested(request) {
    return request.status === RecommendationStatus.REQUESTED;
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {boolean}
   */
  static isAccepted(request) {
    return request.status === RecommendationStatus.ACCEPTED;
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {boolean}
   */
  static isCompletedAndHasParent(request) {
    return RecommendationStatus.isCompleted(request) && request.copiedFromEvaluation !== 0;
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {boolean}
   */
  static isCompletedAndNoParent(request) {
    return RecommendationStatus.isCompleted(request) && request.copiedFromEvaluation === 0;
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @param {number} templateId
   * @returns {boolean}
   */
  static isAutoFilledBy(request, templateId) {
    return RecommendationStatus.isCompleted(request) && request.copiedFromEvaluation === templateId;
  }

  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {boolean}
   */
  static isReUseForFutureEvalsEnable(request) {
    return RecommendationStatus.isCompletedAndNoParent(request) && request.reUseForFutureEvals !== null;
  }
}

export class RecommendationUtil {
  /**
   *
   * @param {import('../store/reducers/dashboardReducer').TEvaluationRequest} request
   * @returns {string}
   */
  static getGroupKey(request) {
    return request.instanceId + request.casId + request.evaluationType;
  }

  /**
   *
   * @param {number} requestId
   * @returns {string}
   */
  static getEvaluationsDisplayNameKey(requestId) {
    return `evaluationsDisplayName_${requestId}`;
  }

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   */
  static getEvaluationDisplayName(request) {
    const result =
      request?.evaluationsDisplayName
        ? request.evaluationsDisplayName
        : RECOMMENDATION_DEFAULT_NAME;
    class MyString extends String {
      toSingleForm = () => result?.replace(/s$/, '') ?? '';
      toPluralForm = () => `${result}s`;
      valueOf = () => result;
    };
    return new MyString(result);
  }

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {string}
   */
  static getApplicantFullName(request) {
    return request?.applicantFirstName
      ? `${request.applicantFirstName} ${request.applicantLastName}`
      : '';
  }

  /**
   *
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationRequest=} request
   * @returns {string}
   */
  static getApplicantFullNameFromRequest(request) {
    return request?.applicantFirstName ? `${request.applicantFirstName} ${request.applicantLastName}` : '';
  }

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   * @param {string=} name
   */
  static getEvalationQuestionBlockByConstantName(request, name) {
    return request?.evaluationFormSection?.evaluationQuestionBlocks?.find((block) => block.constantBlockName === name);
  }

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   * @param {string=} name
   */
  static getEvalationQuestionBlockByName(request, name) {
    return request?.evaluationFormSection?.evaluationQuestionBlocks?.find((block) => block.blockName === name);
  }

  /**
 * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
 * @param {number=} id
 */
  static getEvalationQuestionById(request, id) {
    return request?.evaluationFormSection?.evaluationQuestionBlocks
      ?.flatMap(qBlock => qBlock.evaluationQuestions)
      .filter(qItem => qItem.id === id)?.[0];
  }

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   * @param {string=} name
   */
  static hasQuestionBlockByConstantName(request, name) {
    return !!RecommendationUtil.getEvalationQuestionBlockByConstantName(request, name);
  }

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   */
  static getQuestionRatingsAnswers(request) {
    const ratingsBlock = RecommendationUtil.getEvalationQuestionBlockByConstantName(request, 'Rubrics');
    const answers = ratingsBlock?.evaluationQuestions.map((rating) => {
      const answer = questionUserAnswerObjectResolver(rating.userAnswer)?.answer ?? '';
      return answer;
    });
    return answers;
  }

  /**
   * @param {?import('../store/reducers/dashboardReducer').TEvaluationDetails=} request
   */
  static getQuestionRatingsAnswersStatus(request) {
    const ratingsBlock = RecommendationUtil.getEvalationQuestionBlockByConstantName(request, 'Rubrics');
    const answers = RecommendationUtil.getQuestionRatingsAnswers(request);
    const totalRatings = ratingsBlock?.evaluationQuestions.length ?? 0;
    const totalAnswers = answers?.length ?? 0;
    const totalNotObserved = answers?.filter((answer) => answer === 'Not Observed').length ?? 0;

    return {
      totalRatings,
      totalAnswers,
      totalNotObserved,
      allSetToNotObserved: totalRatings === totalNotObserved,
      complete: totalRatings === totalAnswers - totalNotObserved,
    };
  }
}

export const stripHtmlTags = (/** @type {?string=} */ htmlText) => {
  return htmlText?.replace(/(<([^>]+)>)/gi, '').trim() ?? '';
}

/**
 *
 * @param {Object|Array} requestData
 * @returns {import('../store/reducers/dashboardReducer').TEvaluation|any}
 */
export const removingUids = requestData => {
  if (!(requestData instanceof Object) && !Array.isArray(requestData)) {
    return requestData;
  }

  let nextResult = {};
  for (let key in requestData) {
    if (requestData[key] instanceof Object) {
      requestData[key] = removingUids(requestData[key]);
    } else if (Array.isArray(requestData[key])) {
      requestData[key].forEach(removingUids);
    } else if (key !== '@id') {
      nextResult[key] = requestData[key];
    }
  }
  return nextResult;
};

/**
 * @typedef {import('../store/reducers/dashboardReducer').TEvaluationBlockQuestionUserAnswer} TEvaluationBlockQuestionUserAnswer;
 */

/**
 * @param {void|null|TEvaluationBlockQuestionUserAnswer|TEvaluationBlockQuestionUserAnswer[]} params
 * @returns {null|TEvaluationBlockQuestionUserAnswer}
 */
export const questionUserAnswerObjectResolver = (params) => {
  const answerObj = Array.isArray(params) ? params[0] : params;
  const result = answerObj ?? null;
  return result;
}

/**
 * @param {null|TEvaluationBlockQuestionUserAnswer|TEvaluationBlockQuestionUserAnswer[]} params
 * @returns {TEvaluationBlockQuestionUserAnswer['answer']}
 */
export const questionUserAnswerResolver = (params) => {
  const answerObj = Array.isArray(params) ? params[0] : params;
  const result = answerObj?.answer ?? '';
  return result;
}

/**
 *
 * @param {undefined|null|TEvaluationBlockQuestionUserAnswer} v
 * @returns {{ toArray():TEvaluationBlockQuestionUserAnswer[]; raw():null|TEvaluationBlockQuestionUserAnswer; }}
 */
export const questionUserAnswerObjectBuilder = v => {
  const result = (!v || typeof v.answer === 'undefined' && typeof v.comments === 'undefined')
    ? null
    : { answer: v.answer ?? null, comments: v.comments ?? null }

  return {
    toArray: () => result ? [result] : [],
    raw: () => result,
  }
};
